import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  LinearProgress,
  useTheme,
} from "@mui/material";
import VendorRiskBadge from "../../components/VendorRiskBadge";
import { RISK_FACTORS } from "../../utils/constants";
import { calculateInherentRisk } from "../../utils/riskScoring";
import { getRiskChipColors } from "../../utils/chipStyles";
import useVendorStore from "../../store/useVendorStore";
import { styles } from "../../styles/Vendors.styles";

const InherentRiskPanel = ({ vendor }) => {
  const theme = useTheme();
  const updateRiskInputs = useVendorStore((s) => s.updateRiskInputs);
  const inputs = useMemo(() => vendor.riskInputs || {}, [vendor.riskInputs]);
  const { level, score } = useMemo(() => calculateInherentRisk(inputs), [inputs]);
  const colors = getRiskChipColors(theme, level);

  const handleChange = (key) => (e) => {
    updateRiskInputs(vendor.id, { ...inputs, [key]: e.target.value });
  };

  return (
    <Card sx={styles.mainCard} variant="outlined">
      <CardContent>
        <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong} sx={{ mb: 0.5 }}>
          Inherent Risk Scoring
        </Typography>
        <Typography variant="body2" sx={{ mb: 2.5 }}>
          Set how this vendor interacts with your data and systems. The recommended risk level updates automatically.
        </Typography>

        <Grid container spacing={2}>
          {RISK_FACTORS.map((factor) => (
            <Grid item xs={12} sm={6} key={factor.key}>
              <TextField
                select
                fullWidth
                size="small"
                label={factor.label}
                value={inputs[factor.key] || ""}
                onChange={handleChange(factor.key)}
                helperText={factor.helper}
              >
                {factor.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "8px",
            backgroundColor: colors.bg,
            border: `1px solid ${theme.customColors.border.main}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: colors.color, fontWeight: 600 }}>
              Recommended inherent risk
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <VendorRiskBadge level={level} />
            </Box>
          </Box>
          <Box sx={{ minWidth: 180, flex: 1, maxWidth: 260 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: colors.color }}>
                Risk score
              </Typography>
              <Typography variant="caption" sx={{ color: colors.color, fontWeight: 700 }}>
                {score}/100
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={score}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.customColors.background.main,
                "& .MuiLinearProgress-bar": { backgroundColor: colors.color },
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InherentRiskPanel;
